import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Phone, MapPin } from 'lucide-react';
import { gaEvents } from '@/hooks/useGoogleAnalytics';
import { useRealtorAds, trackRealtorAdClick } from '@/hooks/useRealtorAds';

// Public-safe realtor data (matches realtor_profiles table)
interface RealtorData {
  id?: string;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  phone: string | null;
  address?: string | null;
  city: string | null;
  state: string | null;
  zip?: string | null;
  license_numbers: string | null;
  about_me: string | null;
  logo_url?: string | null;
  title_preference?: string | null;
  ad_image_url?: string | null;
}

interface RealtorCardProps {
  realtor: RealtorData;
  compact?: boolean;
  /** Filter ads by type: 'buyer', 'seller', or null/undefined for random from all */
  adTypeFilter?: 'buyer' | 'seller' | null;
}

export function RealtorCard({ realtor, compact = false, adTypeFilter }: RealtorCardProps) {
  const navigate = useNavigate();
  const fullName = [realtor.first_name, realtor.last_name].filter(Boolean).join(' ');
  // Only show city and state (not full address for privacy)
  const location = [realtor.city, realtor.state].filter(Boolean).join(', ');

  // Use the realtor ads hook for rotation - pass realtor profile id and optional type filter
  const { data: selectedAd } = useRealtorAds(realtor.id || null, adTypeFilter);
  
  // Use rotating ad if available, otherwise fall back to static ad_image_url
  const displayAdUrl = selectedAd?.image_url || realtor.ad_image_url;
  const displayAdId = selectedAd?.id;
  const displayAdType = selectedAd?.ad_type;

  const handleAdClick = () => {
    if (displayAdId) {
      trackRealtorAdClick(displayAdId);
    }
    
    // Navigate based on ad type: buyer → /buyers, seller → /sellers
    if (displayAdType === 'buyer') {
      navigate('/buyers');
    } else if (displayAdType === 'seller') {
      navigate('/sellers');
    } else {
      // Fallback for legacy ads without type - go to sellers page
      navigate('/sellers');
    }
  };

  if (!fullName && !realtor.photo_url && !realtor.about_me) {
    return null;
  }

  if (compact) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-4">
          {realtor.photo_url && (
            <img
              src={realtor.photo_url}
              alt={fullName || 'Realtor'}
              className="w-16 h-16 rounded-lg object-cover shrink-0"
              loading="lazy"
              decoding="async"
              width={64}
              height={64}
            />
          )}
          <div className="min-w-0">
            {fullName && (
              <h4 className="font-semibold text-lg truncate">{fullName}</h4>
            )}
            {realtor.phone && (
              <a 
                href={`tel:${realtor.phone}`} 
                className="text-accent hover:underline text-sm flex items-center gap-1"
                onClick={() => gaEvents.clickCall(realtor.phone || undefined)}
              >
                <Phone className="h-3 w-3" />
                {realtor.phone}
              </a>
            )}
            {realtor.license_numbers && (
              <p className="text-xs text-muted-foreground mt-1">
                License: {realtor.license_numbers}
              </p>
            )}
          </div>
        </div>
      </Card>
    );
  }

  const titleLabel = realtor.title_preference === 'realtor' ? 'Your Local Realtor' : 'Your Local Agent';

  return (
    <Card className="overflow-hidden">
      {/* Active Ad Image at Top - with rotation and click tracking */}
      {displayAdUrl && (
        <div onClick={handleAdClick} className="cursor-pointer">
          <img
            src={displayAdUrl}
            alt={`${fullName || 'Realtor'} - Featured`}
            className="w-full aspect-square object-cover"
            loading="lazy"
            decoding="async"
            width={400}
            height={400}
          />
        </div>
      )}
      
      <div className="p-6">
        <h3 className="text-xl font-display font-bold mb-4">{titleLabel}</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          {realtor.photo_url && (
            <img
              src={realtor.photo_url}
              alt={fullName || 'Realtor'}
              className="w-24 h-24 rounded-lg object-cover shrink-0 mx-auto sm:mx-0"
              loading="lazy"
              decoding="async"
              width={96}
              height={96}
            />
          )}
          <div className="text-center sm:text-left">
            {fullName && (
              <h4 className="font-semibold text-lg">{fullName}</h4>
            )}
            {realtor.phone && (
              <a 
                href={`tel:${realtor.phone}`} 
                className="text-accent hover:underline flex items-center gap-1 justify-center sm:justify-start mt-1"
                onClick={() => gaEvents.clickCall(realtor.phone || undefined)}
              >
                <Phone className="h-4 w-4" />
                {realtor.phone}
              </a>
            )}
            {location && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 justify-center sm:justify-start mt-1">
                <MapPin className="h-4 w-4 shrink-0" />
                {location}
              </p>
            )}
            {realtor.license_numbers && (
              <p className="text-xs text-muted-foreground mt-2">
                License: {realtor.license_numbers}
              </p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
